import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ScheduledDealData } from '../services/dealsApiService';
import { formatDateRange, getTimezoneAbbreviation } from '../utils/timezoneUtils.ts';
import './ScheduledDealsQueue.css';

interface ScheduledDealsQueueProps {
  scheduledDeals: ScheduledDealData[];
  storeId: string; // Added for timezone-aware display
  onViewDeal?: (deal: ScheduledDealData) => void;
  onDeleteDeal?: (deal: ScheduledDealData) => void;
  onDuplicateDeal?: (deal: ScheduledDealData) => void;
  onScheduleNew?: () => void;
  onReorderDeals?: (dealOrder: Array<{ documentId: string; displayOrder: number }>) => void;
  onTogglePause?: (deal: ScheduledDealData) => void;
}

const ScheduledDealsQueue: React.FC<ScheduledDealsQueueProps> = ({
  scheduledDeals,
  storeId,
  onViewDeal,
  onDeleteDeal,
  onDuplicateDeal,
  onScheduleNew,
  onReorderDeals,
  onTogglePause,
}) => {
  // Format date range using store timezone (function imported from utils)
  const formatDealDateRange = (startDate?: Date, endDate?: Date): string => {
    return formatDateRange(startDate, endDate, storeId);
  };

  const getStatusBadge = (deal: ScheduledDealData): JSX.Element => {
    const now = new Date();

    if (deal.status === 'expired') {
      return <span className="status-badge expired">Expired</span>;
    }

    // Paused replaces Live Now / Scheduled so the most actionable state is on top.
    if (deal.isPaused) {
      return <span className="status-badge paused">Paused</span>;
    }

    if (deal.status === 'current') {
      return <span className="status-badge current">Live Now</span>;
    }

    if (deal.startDateTime && deal.startDateTime > now) {
      return <span className="status-badge scheduled">Scheduled</span>;
    }

    return <span className="status-badge scheduled">Scheduled</span>;
  };

  const getMenuTypeBadge = (deal: ScheduledDealData): JSX.Element | null => {
    if (!deal.menuType) return null;

    if (deal.menuType === 'med') {
      return <span className="menu-type-badge medical">Med</span>;
    }

    if (deal.menuType === 'rec') {
      return <span className="menu-type-badge recreational">Rec</span>;
    }

    return null;
  };

  const getDealTypeAndThumbnail = (deal: ScheduledDealData): JSX.Element => {
    if (deal.dealType === 'image' && deal.imageUrls) {
      return (
        <>
          <div className="deal-type-badge">
            <span className="image-indicator">🖼️ Image Deal</span>
            {getMenuTypeBadge(deal)}
            {getStatusBadge(deal)}
          </div>
          <img
            src={deal.imageUrls.desktop || deal.imageUrls.mobile}
            alt="Deal preview"
            className="deal-thumbnail"
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              img.onerror = null;
              if (deal.imageUrls?.mobile && img.src !== deal.imageUrls.mobile) {
                img.src = deal.imageUrls.mobile;
              }
            }}
          />
        </>
      );
    }

    // Text deal with notepad icon instead of thumbnail
    return (
      <>
        <div className="deal-type-badge">
          <span className="text-indicator">📝 Text Deal</span>
          {getMenuTypeBadge(deal)}
          {getStatusBadge(deal)}
        </div>
        <div className="deal-notepad">📝</div>
      </>
    );
  };

  const getExpiredReason = (deal: ScheduledDealData): string => {
    if (deal.status !== 'expired' || !deal.endDateTime) return '';

    const now = new Date();
    const timeSinceExpiry = now.getTime() - deal.endDateTime.getTime();

    if (timeSinceExpiry <= 0) return 'Just expired';

    const hours = Math.floor(timeSinceExpiry / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `Ended ${days} day${days !== 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `Ended ${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      const minutes = Math.floor(timeSinceExpiry / (1000 * 60));
      return `Ended ${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
  };

  // Split deals into active (live now), upcoming (scheduled), and expired.
  // Both active and upcoming sort by displayOrder so the GM's intended ordering
  // persists as a deal transitions from upcoming to active.
  const sortByDisplayOrder = (a: ScheduledDealData, b: ScheduledDealData) => {
    const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return (a.startDateTime?.getTime() || 0) - (b.startDateTime?.getTime() || 0);
  };
  const currentDeals = [...scheduledDeals.filter(deal => deal.status === 'current')].sort(sortByDisplayOrder);
  const upcomingDeals = [...scheduledDeals.filter(deal => deal.status === 'scheduled')].sort(sortByDisplayOrder);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const expiredDeals = scheduledDeals.filter(
    deal => deal.status === 'expired' && deal.endDateTime && deal.endDateTime >= thirtyDaysAgo
  );

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !onReorderDeals) return;
    if (result.source.index === result.destination.index) return;

    // Each section has its own Droppable; route to the right list.
    const sourceList = result.source.droppableId === 'upcoming-deals' ? upcomingDeals : currentDeals;
    const reordered = [...sourceList];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    onReorderDeals(reordered.map((deal, i) => ({ documentId: deal.documentId, displayOrder: i })));
  };

  const hasActiveOrUpcoming = currentDeals.length > 0 || upcomingDeals.length > 0;

  const renderDealItem = (deal: ScheduledDealData, dragHandle?: React.ReactNode) => (
    <>
      <div className="deal-main">
        {dragHandle}
        {getDealTypeAndThumbnail(deal)}
        <div className="deal-info">
          {deal.title && <div className="deal-title">{deal.title}</div>}
          <div className="deal-timing">
            {formatDealDateRange(deal.startDateTime, deal.endDateTime)}
          </div>
        </div>
      </div>
      <div className="deal-actions">
        <button
          onClick={() => onViewDeal?.(deal)}
          className="edit-inline-button"
          title="Edit deal"
          data-testid={`edit-deal-${deal.documentId}`}
        >
          ✏️
        </button>
        {onTogglePause && (
          <button
            onClick={() => onTogglePause(deal)}
            className="pause-button"
            title={deal.isPaused ? 'Resume deal' : 'Pause deal'}
            data-testid={`pause-deal-${deal.documentId}`}
          >
            {deal.isPaused ? '▶️' : '⏸️'}
          </button>
        )}
        <button
          onClick={() => onDeleteDeal?.(deal)}
          className="delete-button"
          title="Delete deal"
          data-testid={`delete-deal-${deal.documentId}`}
        >
          🗑️
        </button>
      </div>
    </>
  );

  return (
    <div className="scheduled-deals-queue" data-testid="scheduled-deals-queue">
      <div className="queue-header">
        <div className="queue-title-section">
          <h3>SCHEDULED DEALS</h3>
          <div className="timezone-info">
            All times shown in {getTimezoneAbbreviation(storeId)} (store local time)
          </div>
        </div>
        <button onClick={() => onScheduleNew?.()} className="schedule-new-button" data-testid="schedule-new-deal-button">
          + Schedule New Deal
        </button>
      </div>

      {!hasActiveOrUpcoming && expiredDeals.length === 0 && (
        <div className="empty-queue">
          <div className="empty-message">
            No scheduled deals yet
          </div>
          <div className="empty-subtitle">
            Schedule deals to automatically publish at specific times
          </div>
        </div>
      )}

      {currentDeals.length > 0 && (
        <div className="deals-section">
          <div className="section-title">
            Active Now:
            {currentDeals.length > 1 && onReorderDeals && (
              <span className="reorder-hint">Carousel order: top = first &middot; Drag to reorder</span>
            )}
          </div>
          {currentDeals.length > 1 && onReorderDeals ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="active-deals">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {currentDeals.map((deal, index) => (
                      <Draggable key={deal.documentId} draggableId={deal.documentId} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`deal-item current ${snapshot.isDragging ? 'dragging' : ''}`}
                            data-testid={`scheduled-deal-${deal.documentId}`}
                          >
                            {renderDealItem(deal,
                              <div className="drag-handle" {...provided.dragHandleProps} title="Drag to reorder">
                                <span className="drag-dots">⠿</span>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            currentDeals.map((deal) => (
              <div
                key={deal.documentId}
                className={`deal-item ${deal.status}`}
                data-testid={`scheduled-deal-${deal.documentId}`}
              >
                {renderDealItem(deal)}
              </div>
            ))
          )}
        </div>
      )}

      {upcomingDeals.length > 0 && (
        <div className="deals-section">
          <div className="section-title">
            Upcoming:
            {upcomingDeals.length > 1 && onReorderDeals && (
              <span className="reorder-hint">Drag to reorder &middot; carries over once live</span>
            )}
          </div>
          {upcomingDeals.length > 1 && onReorderDeals ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="upcoming-deals">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {upcomingDeals.map((deal, index) => (
                      <Draggable key={deal.documentId} draggableId={deal.documentId} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`deal-item ${deal.status} ${snapshot.isDragging ? 'dragging' : ''}`}
                            data-testid={`scheduled-deal-${deal.documentId}`}
                          >
                            {renderDealItem(deal,
                              <div className="drag-handle" {...provided.dragHandleProps} title="Drag to reorder">
                                <span className="drag-dots">⠿</span>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            upcomingDeals.map((deal) => (
              <div
                key={deal.documentId}
                className={`deal-item ${deal.status}`}
                data-testid={`scheduled-deal-${deal.documentId}`}
              >
                {renderDealItem(deal)}
              </div>
            ))
          )}
        </div>
      )}

      {expiredDeals.length > 0 && (
        <div className="deals-section expired-section">
          <div className="section-title">
            Expired ({expiredDeals.length}):
          </div>
          {expiredDeals.map((deal) => (
            <div
              key={deal.documentId}
              className="deal-item expired"
            >
              <div className="deal-main">
                {getDealTypeAndThumbnail(deal)}
                <div className="deal-info">
                  {deal.title && <div className="deal-title">{deal.title}</div>}
                  <div className="deal-timing">
                    {formatDealDateRange(deal.startDateTime, deal.endDateTime)}
                    <span className="expired-reason">{getExpiredReason(deal)}</span>
                  </div>
                </div>
              </div>
              <div className="deal-actions">
                <button
                  onClick={() => onViewDeal?.(deal)}
                  className="edit-inline-button small"
                  title="Edit deal"
                >
                  ✏️
                </button>
                <button
                  onClick={() => onDuplicateDeal?.(deal)}
                  className="duplicate-button small"
                  title="Duplicate this deal"
                >
                  📋
                </button>
                <button
                  onClick={() => onDeleteDeal?.(deal)}
                  className="delete-button small"
                  title="Delete expired deal"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduledDealsQueue;
